import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Trophy, ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CouponApplyField from "@/components/checkout/CouponApplyField";
import { WalletPayPanel } from "@/components/vendor/WalletPayPanel";
import { useWalletBalance } from "@/hooks/useWalletBalance";

/**
 * BoostCampaignDialog — A4 vendor-facing "Boost this campaign" flow.
 *
 * Phase 18 Round 2B — cutover from Razorpay Checkout to wallet debit.
 * The flow now hits POST /featured/pay-with-wallet directly; on
 * insufficient balance the shared WalletPayPanel flips to "short by"
 * state with a top-up CTA.
 *
 * Renew semantics: if the vendor already owns the slot at (category,
 * city, rank), the backend flags it as own-renewal and the debit
 * extends the expiry rather than starting fresh.
 */

const BUSINESS_TYPES = [
  "Cafe", "Bakery & Sweets", "Retail Store", "Supermarket & Grocery",
  "Clothing & Fashion", "Footwear", "Jewellery", "Electronics & Mobiles",
  "Salon & Spa", "Pharmacy & Healthcare", "Optical & Eyewear",
  "Furniture & Home Decor", "Automobile (Showroom/Service)",
  "Hotel & Hospitality", "Gym & Fitness", "Education & Coaching",
  "Events", "Other",
];

const rupees = (paise) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(Math.round((Number(paise) || 0) / 100));

export default function BoostCampaignDialog({ open, onOpenChange, campaign, onSuccess }) {
  const [pricing, setPricing] = useState(null);
  const [rank, setRank] = useState("1");
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [paying, setPaying] = useState(false);
  // Coupon state — cleared whenever the (rank, category, city) tuple
  // changes since the underlying base price could differ.
  const [couponApplied, setCouponApplied] = useState(null);

  // Wallet balance (only fetched while the dialog is open — no need
  // to keep it warm otherwise).
  const wallet = useWalletBalance({ autoLoad: open });

  // Reset applied coupon when rank changes (price could differ per rank).
  useEffect(() => {
    if (couponApplied) setCouponApplied(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rank]);

  // Load pricing + vendor defaults once the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [p, m] = await Promise.all([api.featuredPricing(), api.me()]);
        if (cancelled) return;
        setPricing(p.data);
        // Auto-fill from campaign-level override, falling back to vendor.
        setCategory((campaign?.category || m.data?.business_type || "Other").trim());
        setCity((campaign?.campaign_city || m.data?.city || "").trim());
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load Boost options");
      }
    })();
    return () => { cancelled = true; };
  }, [open, campaign]);

  // Live quote whenever the (rank, category, city) tuple changes.
  useEffect(() => {
    if (!open || !campaign?.id || !category || !city) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoting(true);
    api.featuredQuote({
      campaign_id: campaign.id,
      category,
      city,
      rank: Number(rank),
    })
      .then((r) => { if (!cancelled) setQuote(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setQuote(null);
        toast.error(e?.response?.data?.detail || "Couldn't fetch quote");
      })
      .finally(() => { if (!cancelled) setQuoting(false); });
    return () => { cancelled = true; };
  }, [open, campaign?.id, rank, category, city]);

  const price = useMemo(() => {
    if (!pricing) return null;
    return pricing.rank_prices_paise?.[String(rank)];
  }, [pricing, rank]);

  // Effective price after any applied coupon, in paise. Featured
  // pricing stores no GST, so this equals the wallet debit amount.
  const effectivePricePaise = useMemo(() => {
    if (price == null) return 0;
    const discount = Number(couponApplied?.discount || 0) * 100;
    return Math.max(0, Number(price) - discount);
  }, [price, couponApplied]);

  const handleBoost = async () => {
    if (paying) return;
    if (!campaign?.id) return;
    if (!category || !city) {
      toast.error("Please pick a category and city first");
      return;
    }
    if (quote?.occupied) {
      toast.error("That slot is currently held by another vendor. Try a different rank or wait for it to expire.");
      return;
    }
    setPaying(true);
    try {
      // Phase 18 Round 2B — wallet-debit single-shot flow. Replaces
      // the previous two-phase Razorpay `purchase` → `convert-to-paid`
      // handshake. Insufficient balance surfaces as a 402 which the
      // shared WalletPayPanel visualises.
      const r = await api.featuredPayWithWallet({
        campaign_id: campaign.id,
        category,
        city,
        rank: Number(rank),
        coupon_code: couponApplied?.code || null,
      });
      toast.success(
        `Boost active${quote?.is_own ? " — slot renewed" : ""}! Expires ${new Date(r.data.expires_at).toLocaleDateString()}.`,
      );
      onOpenChange?.(false);
      onSuccess?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 402 && detail?.code === "insufficient_wallet_balance") {
        if (Number.isFinite(Number(detail.balance_paise))) {
          wallet.setBalancePaise(Number(detail.balance_paise));
        }
        toast.error(detail.message || "Not enough wallet balance.");
      } else if (status === 409 && detail?.code === "slot_occupied") {
        toast.error(detail.message || "That slot is currently held by another vendor.");
      } else if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || e?.message || "Boost failed");
      }
    } finally {
      setPaying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="boost-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#F5A800]" /> Boost this campaign
          </DialogTitle>
          <DialogDescription>
            Buy a Top-3 slot in your (category, city) — your campaign floats above everyone else on Explore + Homepage for {pricing?.duration_days || 30} days.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="boost-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Mumbai"
                data-testid="boost-city-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Rank</Label>
            <RadioGroup value={rank} onValueChange={setRank} className="grid grid-cols-3 gap-2">
              {["1", "2", "3"].map((r) => {
                const p = pricing?.rank_prices_paise?.[r];
                return (
                  <label
                    key={r}
                    htmlFor={`rank-${r}`}
                    className={`cursor-pointer rounded-lg border-2 p-3 flex flex-col items-center gap-1 transition-colors ${rank === r ? "border-[#F5A800] bg-[#F5A800]/10" : "border-border hover:border-[#F5A800]/50"}`}
                    data-testid={`boost-rank-option-${r}`}
                  >
                    <RadioGroupItem id={`rank-${r}`} value={r} className="sr-only" />
                    <span className="font-heading text-2xl font-bold">Top {r}</span>
                    <span className="text-sm text-muted-foreground">{p != null ? rupees(p) : "—"}</span>
                  </label>
                );
              })}
            </RadioGroup>
          </div>

          <div className="rounded-lg border border-border p-3 text-sm space-y-1 bg-muted/30" data-testid="boost-quote">
            {quoting ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking availability…
              </span>
            ) : quote?.occupied ? (
              <span className="flex items-center gap-2 text-destructive font-medium">
                <ShieldAlert className="h-4 w-4" /> Slot occupied by another vendor.
                {quote.current_slot_expires_at && (
                  <span className="text-muted-foreground font-normal ml-1">
                    Frees on {new Date(quote.current_slot_expires_at).toLocaleDateString()}.
                  </span>
                )}
              </span>
            ) : quote?.is_own ? (
              <span className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-medium">
                <RefreshCw className="h-4 w-4" /> You currently hold this slot — this is a renewal.
                {quote.current_slot_expires_at && (
                  <span className="text-muted-foreground font-normal ml-1">
                    Extends past {new Date(quote.current_slot_expires_at).toLocaleDateString()}.
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">
                Slot is available. You&apos;ll be charged {price != null ? rupees(price) : "—"} for {pricing?.duration_days || 30} days.
              </span>
            )}
          </div>

          {!quote?.occupied && price != null && (
            <div className="space-y-3">
              <div data-testid="boost-coupon-block">
                <CouponApplyField
                  testIdPrefix="boost-coupon"
                  applied={couponApplied}
                  disabled={paying || quoting}
                  onRemove={() => setCouponApplied(null)}
                  onApply={async (code) => {
                    const baseRupees = Number(price) / 100;
                    const r = await api.previewBillingCoupon({
                      code,
                      base_amount: baseRupees,
                      surface: "featured_slot",
                      // Round 2B — pass checkout_city for franchise
                      // city_scope validation, matching the confirm
                      // endpoint's fallback logic.
                      checkout_city: campaign?.campaign_city || city || null,
                    });
                    setCouponApplied(r.data.billing_coupon);
                    toast.success(
                      `Coupon ${r.data.billing_coupon.code} applied — you save ₹${Number(r.data.billing_coupon.discount).toFixed(2)}`,
                    );
                  }}
                />
              </div>
              {/* Phase 18 Round 2B — wallet balance + pay CTA in one
                  reusable panel. On insufficient balance the panel
                  flips to "short by ₹X" with a top-up link and
                  hides the pay button; the vendor tops up in a new
                  tab and hits "I've topped up" to refresh. */}
              <WalletPayPanel
                loading={wallet.loading}
                error={wallet.error}
                balancePaise={wallet.balancePaise}
                promoPaise={wallet.promoPaise}
                requiredPaise={effectivePricePaise}
                busy={paying}
                onPay={handleBoost}
                onRefresh={wallet.refresh}
                payLabel={`${quote?.is_own ? "Renew" : "Boost"} for ${rupees(effectivePricePaise)}`}
                compact
                testIdPrefix="boost-wallet"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="boost-cancel">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
